import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useCallback, useEffect } from 'react';
import { Calendar, ArrowRight, ArrowLeft, CaretLeft, CaretRight } from '@phosphor-icons/react';
import useEmblaCarousel from 'embla-carousel-react';

const blogImages = [
  'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/7pd5e9re_chittaway07.jpg',
  'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/8ms1nkkg_chittaway01.jpg',
  'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/0rnorpmk_chittaway02.jpg',
  'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/l8phr7vu_chittaway04.jpg',
  'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/70c0zta7_chittaway08.jpg',
  'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/bhsnurr7_chittaway05.jpg',
  'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/1m8x6vtg_chittaway09.jpg',
];

const blogPosts = [
  {
    id: 1,
    title: 'Central Coast Hotel Renovation: How We Took It Back to the Shell and Built Something Worth Talking About',
    excerpt: 'Some jobs you finish and walk away. This one, you stand back and stare. A full commercial hotel renovation on the Central Coast — stripped back to the shell and rebuilt from the ground up.',
    date: 'April 2026',
    image: 'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/7pd5e9re_chittaway07.jpg',
    placeholder: false,
    fullPost: true,
    images: blogImages,
    sections: [
      {
        text: 'Some jobs you finish and walk away. This one, you stand back and stare.',
        isLead: true,
      },
      {
        text: 'We recently completed a full commercial hotel renovation here on the Central Coast. The brief was clear: strip it out, start fresh, and build something that actually works.',
      },
      {
        heading: 'We went back to the shell',
        text: 'The first step was removing all interior linings. Everything came out. We took the space back to a bare shell before a single new element went in. That\'s the only way to do a renovation like this properly.',
      },
      {
        heading: 'New false ceilings done right',
        text: 'The existing false ceilings were removed in full. We installed new furring channels throughout to achieve high ceilings tied back to the original building structure. The result opened the space up completely and gave the venue a character it never had before.',
      },
      {
        heading: 'Commercial bar and kitchen',
        text: 'We built a brand new commercial bar and kitchen from scratch. Designed for volume, built to code, and finished to a level that holds up under daily trade.',
      },
      {
        heading: 'Indoor and outdoor fireplaces',
        text: 'New fireplaces were installed throughout the renovation, both inside and out. They give the space a warmth and homeliness that sets this hotel apart.',
      },
      {
        heading: 'Outdoor area and beer garden sunroom',
        text: 'The build included a new outdoor entertaining area and a sunroom structure for the beer garden. Both were designed to extend the venue\'s capacity and give patrons a reason to stay longer.',
      },
      {
        heading: 'Outdoor play area for the kids',
        text: 'We built a dedicated outdoor play area with synthetic grass, a soft fall setup, and quality outdoor play equipment. Families have a reason to stay longer, and the kids have a space that\'s built for them.',
      },
      {
        heading: 'The result',
        text: 'The finishing result was incredible. This is exactly the kind of project we love on the Central Coast: complex, detailed, and deeply satisfying when it comes together.',
      },
      {
        text: 'If you have a commercial build, fitout, or renovation on the Central Coast and you want it done properly, call Rimsie Constructions Pty Ltd.',
        isCta: true,
      },
    ],
  },
  {
    id: 2,
    title: 'Central Coast Knock Down Rebuild: Architecturally Designed from the Ground Up',
    excerpt: 'Some homes you renovate. Some you start over. This one deserved a fresh start. A full knock down rebuild on the Central Coast — architecturally designed and built from the ground up.',
    date: 'April 2026',
    image: 'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/973c47l5_Rimsie_website4.jpg',
    placeholder: false,
    fullPost: true,
    images: [
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/973c47l5_Rimsie_website4.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/0sq2pq2h_Rimsie_website5.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/2fkiqkh1_Rimsie_website6.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/jaxh12a3_Rimsie_website.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/8ncv11bf_Rimsie_website2.jpg',
      'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/vi8q2l7g_Rimsie_website3.jpg',
    ],
    sections: [
      {
        text: 'Some homes you renovate. Some you start over. This one deserved a fresh start.',
        isLead: true,
      },
      {
        text: 'We recently completed a full knock down rebuild on the Central Coast. The brief was clear: demolish what was there, design something better, and build the owners their dream home.',
      },
      {
        heading: 'Full knock down rebuild',
        text: 'The existing structure came down completely. We cleared the site and started from scratch. That gave us the freedom to get the design and the build right from the ground up.',
      },
      {
        heading: 'Architecturally designed features throughout',
        text: 'This wasn\'t a standard rebuild. Architectural design features were incorporated throughout the entire home, giving every room a considered finish that lifted the whole project.',
      },
      {
        heading: 'Two storey split level construction',
        text: 'The home was designed and built across two storeys on a split level. This type of construction takes precision. The layout, the levels, and the transitions between spaces all had to work together.',
      },
      {
        heading: 'High end finishes',
        text: 'Every finish was selected to match the standard the owners were after. The details matter on a build like this, and nothing was left to chance.',
      },
      {
        heading: 'The result',
        text: 'The owners got their dream home. That\'s the job. That\'s why we build.',
      },
      {
        text: 'If you have a residential project on the Central Coast and you want it done properly, call Rimsie Constructions Pty Ltd.',
        isCta: true,
      },
    ],
  },
  {
    id: 3,
    title: 'Blog Post Coming Soon',
    excerpt: 'Stay tuned for updates on our latest projects and construction tips from Brett.',
    date: 'Coming Soon',
    image: 'https://customer-assets.emergentagent.com/job_build-website-154/artifacts/jaxh12a3_Rimsie_website.jpg',
    placeholder: true,
  },
];

/* ── Blog Carousel ──────────────────────────────────── */
const BlogCarousel = ({ images }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [currentSlide, setCurrentSlide] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrentSlide(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  return (
    <div className="relative my-12" data-testid="blog-carousel">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {images.map((img, idx) => (
            <div key={`blog-slide-${img.slice(-20)}`} className="flex-[0_0_100%] min-w-0">
              <div className="aspect-[16/9] overflow-hidden">
                <img src={img} alt={`Project photo ${idx + 1}`} className="w-full h-full object-cover" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <button onClick={scrollPrev}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-primary-bg/70 hover:bg-primary-bg/90 text-heading transition-colors"
        aria-label="Previous photo">
        <CaretLeft size={22} weight="light" />
      </button>
      <button onClick={scrollNext}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-primary-bg/70 hover:bg-primary-bg/90 text-heading transition-colors"
        aria-label="Next photo">
        <CaretRight size={22} weight="light" />
      </button>

      {/* Dots + counter */}
      <div className="flex items-center justify-center gap-2 mt-4">
        {images.map((_, idx) => (
          <button key={`blog-dot-${idx}`} onClick={() => emblaApi && emblaApi.scrollTo(idx)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              idx === currentSlide ? 'bg-accent w-6' : 'bg-heading/20 w-3'
            }`}
            aria-label={`Go to photo ${idx + 1}`} />
        ))}
        <span className="text-body-muted text-xs ml-3">{currentSlide + 1} / {images.length}</span>
      </div>
    </div>
  );
};

/* ── Full Blog Post View ────────────────────────────── */
const BlogPostView = ({ post, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-primary-bg z-50 overflow-y-auto"
      data-testid="blog-post-view"
    >
      {/* Sticky header */}
      <div className="sticky top-0 z-10 glass-header">
        <div className="container-custom flex items-center justify-between py-4">
          <button onClick={onClose} data-testid="blog-back-button"
            className="flex items-center gap-2 text-heading/70 hover:text-heading transition-colors">
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back to Blog</span>
          </button>
          <span className="text-body-muted text-sm">{post.date}</span>
        </div>
      </div>

      {/* Hero image */}
      <div className="w-full h-[50vh] md:h-[60vh] relative overflow-hidden">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-bg via-primary-bg/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="container-custom">
            <p className="overline mb-4">Rimsie Constructions Pty Ltd</p>
            <h1 className="text-3xl md:text-5xl font-heading font-light text-heading leading-tight max-w-4xl">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Article content */}
      <article className="container-custom max-w-4xl py-16">
        {post.sections.map((section, idx) => (
          <div key={section.heading || `section-${idx}`} className={`${idx > 0 ? 'mt-10' : ''}`}>
            {section.isLead && (
              <p className="text-2xl md:text-3xl font-heading font-light text-heading leading-relaxed border-l-2 border-accent pl-6 md:pl-8">
                {section.text}
              </p>
            )}

            {section.isCta && (
              <div className="bg-secondary-bg border border-border-subtle p-8 md:p-12 text-center mt-16">
                <p className="text-heading text-xl md:text-2xl font-heading font-light leading-relaxed mb-6">
                  {section.text}
                </p>
                <a href="tel:0416283443" className="btn-primary inline-block">
                  Call Brett — 0416 283 443
                </a>
              </div>
            )}

            {!section.isLead && !section.isCta && (
              <>
                {section.heading && (
                  <h2 className="text-2xl md:text-3xl font-heading font-light text-heading mb-4">
                    {section.heading}
                  </h2>
                )}
                <p className="text-body-text text-lg leading-relaxed">
                  {section.text}
                </p>
              </>
            )}

            {/* Insert carousel after the first regular paragraph */}
            {idx === 1 && post.images && (
              <BlogCarousel images={post.images} />
            )}
          </div>
        ))}

        {/* Divider */}
        <div className="border-t border-border-subtle mt-16 pt-8 flex items-center justify-between">
          <div>
            <p className="text-body-muted text-sm">Written by</p>
            <p className="text-heading font-heading">Brett Rimmer</p>
            <p className="text-body-muted text-sm">Director, Rimsie Constructions Pty Ltd</p>
          </div>
          <button onClick={onClose} className="btn-secondary text-sm">Back to Blog</button>
        </div>
      </article>
    </motion.div>
  );
};

/* ── Blog Section ───────────────────────────────────── */
const Blog = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedPost, setSelectedPost] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      <section id="blog" data-testid="blog-section" className="section-padding bg-primary-bg" ref={ref}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="overline mb-4">News & Updates</p>
            <h2 data-testid="blog-heading"
              className="text-4xl sm:text-5xl font-heading font-light text-heading">
              From the Build Site
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {blogPosts.map((post) => (
              <motion.article
                key={post.id}
                variants={itemVariants}
                transition={{ duration: 0.6 }}
                data-testid={`blog-post-${post.id}`}
                className={`bg-secondary-bg border border-border-subtle card-hover group ${
                  post.fullPost ? 'cursor-pointer' : ''
                }`}
                onClick={() => post.fullPost && setSelectedPost(post)}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover image-zoom" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-body-muted text-sm mb-4">
                    <Calendar size={16} />
                    <span>{post.date}</span>
                  </div>
                  <h3 className="text-heading text-lg font-heading mb-3 line-clamp-2">{post.title}</h3>
                  <p className="text-body-text text-sm leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                  {post.fullPost && (
                    <span className="inline-flex items-center gap-2 text-accent text-sm font-medium group-hover:gap-3 transition-all">
                      Read Article <ArrowRight size={16} />
                    </span>
                  )}
                  {post.placeholder && (
                    <span className="inline-flex items-center gap-2 text-accent/50 text-sm">Content coming soon</span>
                  )}
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {selectedPost && (
        <BlogPostView post={selectedPost} onClose={() => setSelectedPost(null)} />
      )}
    </>
  );
};

export default Blog;
